import { couponApis } from "@payping/api";
import type { CouponDetailViewModel } from "@payping/api";
import { Book, CuToman } from "@payping/icons";
import { getCouponDetails } from "@payping/redux/actions";
import { DashboardRoutes } from "@/router/constant";
import { toJalali } from "@payping/utils/dateUtils";
import { ensureString } from "@payping/utilities-shared/string";
import {
  Button,
  Col,
  Descriptions,
  Drawer,
  Flex,
  Row,
  Skeleton,
  Space,
  Tag,
  Typography,
  usePaypingContext,
} from "@payping/web-components";
import { Dispatch, FC, SetStateAction } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAsyncRetry } from "react-use";

import { useAppDispatch, useAppTranslation } from "@/utils";
import { LocationStateSearch } from "@/pages/Shop/Coupon/types";
import { SwitchRow } from "@/core/component";

interface Props {
  retryList: () => void;
  code: string;
  setCode: Dispatch<SetStateAction<string | undefined>>;
}

const CouponDetail: FC<Props> = (props) => {
  const { code, setCode, retryList } = props;
  const location = useLocation();
  const locationSearch = (location.state as LocationStateSearch)?.locationSearch;
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const { breakpoints } = usePaypingContext();
  const { md } = breakpoints;

  const {
    loading: loadingCoupon,
    retry,
    value: couponDetails,
  } = useAsyncRetry(
    (): Promise<CouponDetailViewModel | undefined> => dispatch(getCouponDetails(code)),
    [code, dispatch],
  );

  const buttons = (
    <Space>
      <Link
        to={{ pathname: DashboardRoutes.myShop.coupon.edit.url.replace(":code", code) }}
        state={{ locationSearch }}
        key={1}
      >
        <Button type={"primary"}>{t("edit")}</Button>
      </Link>
    </Space>
  );

  function getTagColor(name: string) {
    switch (true) {
      // TODO: sadly backend returns persian name
      case name === "همه آیتم‌ها":
      case name === "همه فاکتورها":
        return "blue";
      default:
        return "magenta";
    }
  }

  return (
    <Drawer
      extra={buttons}
      title={t("pageTitles.couponDetail")}
      placement={md ? "left" : "bottom"}
      size={md ? undefined : "100%"}
      open={Boolean(code)}
      onClose={() => setCode(undefined)}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          {loadingCoupon ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={<>&nbsp;</>}>
              <Descriptions.Item label={t("coupon.table.name")}>
                {
                  <div className={"tw:flex tw:justify-between"}>
                    {couponDetails?.name}
                    <Button
                      size={"small"}
                      type={"primary"}
                      icon={<Book height={14} width={14} />}
                      onClick={async () => {
                        await couponApis.couponChangeArchive(code);
                        retry();
                        retryList();
                      }}
                    >
                      {couponDetails?.isArchived ? t("operations.restore") : t("operations.archive")}
                    </Button>
                  </div>
                }
              </Descriptions.Item>
              <Descriptions.Item label={t("coupon.table.code")}>{couponDetails?.userCouponCode}</Descriptions.Item>
              <Descriptions.Item label={t("expireDate")}>
                {toJalali({ date: couponDetails?.redeemDateTime, toFormat: "YYYY/MM/DD HH:mm" }).string}
              </Descriptions.Item>
              <Descriptions.Item label={t("createDate")}>
                {toJalali({ date: couponDetails?.createdOn, toFormat: "YYYY/MM/DD HH:mm" }).string}
              </Descriptions.Item>
              <Descriptions.Item label={t("coupon.table.amount")}>
                {couponDetails?.type === 0 ? (
                  `${String(couponDetails?.amount)}%`
                ) : (
                  <Flex align="center">
                    <Typography.Text type="secondary">{String(couponDetails?.amount)}</Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                )}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        <Col xs={24}>
          {loadingCoupon ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={<>&nbsp;</>}>
              <Descriptions.Item label={t("coupon.detail.buyerCounts")}>
                {ensureString({ text: String(couponDetails?.buyerCounts ?? ""), fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("coupon.detail.buyerSum")}>
                <Flex align="center">
                  <Typography.Text type="secondary">
                    {ensureString({ text: String(couponDetails?.buyerSum ?? ""), fallbackValue: "-" })}
                  </Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        <Col xs={24}>
          <SwitchRow
            label={t("coupon.detail.statusActive")}
            onChange={async () => {
              await couponApis.couponChangeActivation(code);
              retry();
              retryList();
            }}
          />
        </Col>
        <Col xs={24}>
          <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
            {t("coupon.detail.attributionTo")}
            <Space>
              {couponDetails?.activeProductName?.map((item, index) => (
                <Tag key={index} color={getTagColor(item)}>
                  {item}
                </Tag>
              ))}
            </Space>
          </Space>
        </Col>
      </Row>
    </Drawer>
  );
};

export default CouponDetail;
